<ion-content>
  <div class="page-wrapper">

    <div class="page-header">
      <h1>{{ 'docs.title' | translate }}</h1>
      <p class="lead">
        {{ 'docs.lead' | translate }}
        {{ 'docs.tryItOutIntro' | translate }} <a routerLink="/playground">Playground</a>.
      </p>
    </div>

    <!-- ── Basis-URL ──────────────────────────────────────────────────────── -->
    <ion-card class="doc-card">
      <ion-card-content>
        <h2>{{ 'docs.baseUrl.title' | translate }}</h2>
        <p>
          {{ 'docs.baseUrl.pathIntro' | translate }} <code>/api/v1</code>.
          {{ 'docs.baseUrl.hostInfo' | translate }} <code>devApiUrl</code>
          {{ 'docs.baseUrl.sameAsPlayground' | translate }} <a routerLink="/playground">Playground</a>
          {{ 'docs.baseUrl.usesForRequests' | translate }}
        </p>
      </ion-card-content>
    </ion-card>

    <!-- ── Authentifizierung ──────────────────────────────────────────────── -->
    <ion-card class="doc-card">
      <ion-card-content>
        <h2>{{ 'docs.auth.title' | translate }}</h2>
        <p>
          {{ 'docs.auth.everyRequestNeeds' | translate }} <code>Authorization</code>{{ 'docs.auth.headerNeeded' | translate }}
        </p>
        <pre class="code-block">Authorization: Bearer gmpat_...</pre>
        <p>
          {{ 'docs.auth.createTokenIntro' | translate }} <a routerLink="/tokens">{{ 'tokens.title' | translate }}</a>.
          {{ 'docs.auth.createTokenOutro' | translate }}
        </p>

        <h3>{{ 'docs.scopes.title' | translate }}</h3>
        <p>
          {{ 'docs.scopes.intro' | translate }}
          (<code>TableStack</code>, <code>Speisekarte</code>)
          {{ 'docs.scopes.levelInfo' | translate }}
          (<code>Kein Zugriff</code> / <code>Lesen</code> / <code>Schreiben</code>).
          {{ 'docs.scopes.requirement' | translate }} <code>403 Forbidden</code>.
        </p>
        <ul class="note-list">
          <li>{{ 'docs.scopes.note1' | translate }}</li>
          <li>{{ 'docs.scopes.note2' | translate }}</li>
          <li>
            <code>Zugriff</code> {{ 'docs.scopes.note3' | translate }}
          </li>
        </ul>
      </ion-card-content>
    </ion-card>

    <!-- ── Rate-Limiting ──────────────────────────────────────────────────── -->
    <ion-card class="doc-card">
      <ion-card-content>
        <h2>{{ 'docs.rateLimit.title' | translate }}</h2>
        <p>{{ 'docs.rateLimit.intro' | translate }}</p>
        <ul class="note-list">
          <li>{{ 'docs.rateLimit.general' | translate }}</li>
          <li>{{ 'docs.rateLimit.reservationStatus' | translate }} <code>PATCH /tablestack/reservations/&#123;id&#125;</code>.</li>
          <li>{{ 'docs.rateLimit.retryAfter' | translate }} <code>429 Too Many Requests</code> {{ 'docs.rateLimit.retryAfterSuffix' | translate }} <code>Retry-After</code>.</li>
        </ul>
      </ion-card-content>
    </ion-card>

    <!-- ── Endpunkte ──────────────────────────────────────────────────────── -->
    <div class="section-heading">
      <h2>{{ 'docs.endpoints.title' | translate }}</h2>
      <p>{{ 'docs.endpoints.subtitle' | translate }}</p>
    </div>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--get">GET</span>
          <code class="endpoint-path">/company</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.scope' | translate }} <code>Unternehmen: {{ 'docs.level.read' | translate }}</code></p>
        <p>{{ 'docs.endpoints.company.description' | translate }}</p>
      </ion-card-content>
    </ion-card>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--get">GET</span>
          <code class="endpoint-path">/departments</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.scope' | translate }} <code>Abteilungen: {{ 'docs.level.read' | translate }}</code></p>
        <p>{{ 'docs.endpoints.departments.description' | translate }}</p>
      </ion-card-content>
    </ion-card>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--get">GET</span>
          <code class="endpoint-path">/employees</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.scope' | translate }} <code>Mitarbeiter: {{ 'docs.level.read' | translate }}</code></p>
        <p>{{ 'docs.endpoints.employees.description' | translate }}</p>
      </ion-card-content>
    </ion-card>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--get">GET</span>
          <code class="endpoint-path">/tablestack/reservations</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.scope' | translate }} <code>TableStack: {{ 'docs.level.read' | translate }}</code></p>
        <p>{{ 'docs.endpoints.reservationsList.description' | translate }}</p>

        <h4>{{ 'docs.queryParams' | translate }}</h4>
        <table class="param-table">
          <thead><tr><th>{{ 'docs.table.parameter' | translate }}</th><th>{{ 'docs.table.type' | translate }}</th><th>{{ 'docs.table.description' | translate }}</th></tr></thead>
          <tbody>
            <tr><td><code>page</code></td><td>int</td><td>{{ 'docs.endpoints.reservationsList.pageParam' | translate }}</td></tr>
            <tr><td><code>pageSize</code></td><td>int</td><td>{{ 'docs.endpoints.reservationsList.pageSizeParam' | translate }}</td></tr>
            <tr><td><code>date</code></td><td>{{ 'docs.endpoints.reservationsList.dateType' | translate }}</td><td>{{ 'docs.endpoints.reservationsList.dateParam' | translate }}</td></tr>
            <tr><td><code>status</code></td><td>string</td><td>{{ 'docs.endpoints.reservationsList.statusParam' | translate }} <code>Pending</code>, <code>Confirmed</code>, <code>Cancelled</code>, <code>Rejected</code>, <code>NoShow</code></td></tr>
          </tbody>
        </table>

        <h4>{{ 'docs.response' | translate }} <span class="status-badge status-badge--2xx">200</span></h4>
        <pre class="code-block">&#123;
  "items": [
    &#123;
      "id": "…",
      "guestName": "Maria Keller",
      "guestEmail": "maria@example.com",
      "guestPhone": "+41791234567",
      "reservationAt": "2026-07-10T19:00:00+02:00",
      "partySize": 4,
      "status": "Confirmed",
      "notes": null
    &#125;
  ],
  "total": 1,
  "page": 1,
  "pageSize": 25
&#125;</pre>
        <p class="note">{{ 'docs.endpoints.reservationsList.note' | translate }}</p>
      </ion-card-content>
    </ion-card>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--get">GET</span>
          <code class="endpoint-path">/tablestack/reservations/&#123;id&#125;</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.scope' | translate }} <code>TableStack: {{ 'docs.level.read' | translate }}</code></p>
        <p>{{ 'docs.endpoints.reservationOne.description' | translate }} <code>404 Not Found</code> {{ 'docs.endpoints.reservationOne.notFound' | translate }}</p>
      </ion-card-content>
    </ion-card>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--post">POST</span>
          <code class="endpoint-path">/tablestack/reservations</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.scope' | translate }} <code>TableStack: {{ 'docs.level.write' | translate }}</code></p>
        <p>{{ 'docs.endpoints.reservationCreate.description' | translate }}</p>
        <p class="note">{{ 'docs.endpoints.reservationCreate.note' | translate }}</p>
      </ion-card-content>
    </ion-card>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--patch">PATCH</span>
          <code class="endpoint-path">/tablestack/reservations/&#123;id&#125;</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.scope' | translate }} <code>TableStack: {{ 'docs.level.write' | translate }}</code></p>
        <p>{{ 'docs.endpoints.reservationStatus.description' | translate }}</p>
        <p class="note">{{ 'docs.endpoints.reservationStatus.note' | translate }}</p>
      </ion-card-content>
    </ion-card>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--get">GET</span>
          <code class="endpoint-path">/menu/items</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.scope' | translate }} <code>Speisekarte: {{ 'docs.level.read' | translate }}</code></p>
        <p>{{ 'docs.endpoints.menuList.description' | translate }}</p>

        <h4>{{ 'docs.response' | translate }} <span class="status-badge status-badge--2xx">200</span></h4>
        <pre class="code-block">&#123;
  "items": [
    &#123;
      "id": "…",
      "menuId": "…",
      "menuName": "Mittagskarte",
      "name": "Rösti mit Speck",
      "description": "Hausgemachte Rösti, Speckwürfel, Spiegelei",
      "category": "Hauptgang",
      "price": 18.50,
      "currency": "CHF",
      "isAvailable": true
    &#125;
  ]
&#125;</pre>
      </ion-card-content>
    </ion-card>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--patch">PUT</span>
          <code class="endpoint-path">/menu/items/&#123;id&#125;</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.scope' | translate }} <code>Speisekarte: {{ 'docs.level.write' | translate }}</code></p>
        <p>{{ 'docs.endpoints.menuUpdate.description' | translate }}</p>
      </ion-card-content>
    </ion-card>

    <ion-card class="doc-card endpoint-card">
      <ion-card-content>
        <div class="endpoint-head">
          <span class="method-badge method-badge--get">GET</span>
          <code class="endpoint-path">/openapi.json</code>
        </div>
        <p class="endpoint-scope">{{ 'docs.endpoints.noAuthRequired' | translate }}</p>
        <p>{{ 'docs.endpoints.openapi.description' | translate }}</p>
      </ion-card-content>
    </ion-card>

    <!-- ── Fehlerantworten ────────────────────────────────────────────────── -->
    <ion-card class="doc-card">
      <ion-card-content>
        <h2>{{ 'docs.errors.title' | translate }}</h2>
        <table class="param-table">
          <thead><tr><th>{{ 'docs.table.status' | translate }}</th><th>{{ 'docs.table.meaning' | translate }}</th></tr></thead>
          <tbody>
            <tr><td><span class="status-badge status-badge--4xx">401</span></td><td>{{ 'docs.errors.401' | translate }}</td></tr>
            <tr><td><span class="status-badge status-badge--4xx">403</span></td><td>{{ 'docs.errors.403' | translate }}</td></tr>
            <tr><td><span class="status-badge status-badge--4xx">404</span></td><td>{{ 'docs.errors.404' | translate }}</td></tr>
            <tr><td><span class="status-badge status-badge--4xx">429</span></td><td>{{ 'docs.errors.429' | translate }}</td></tr>
          </tbody>
        </table>
        <p>
          {{ 'docs.errors.bodyFormat' | translate }} <code class="inline-code">&#123; "error": "forbidden", "message": "..." &#125;</code>.
        </p>
      </ion-card-content>
    </ion-card>

  </div>
</ion-content>
